import { useState, useEffect, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useShallow } from 'zustand/react/shallow';
import _ from 'lodash';
import NiceModal from '@ebay/nice-modal-react';
import { differenceInCalendarDays } from 'date-fns';

// Import components
import { PaymentSchedule } from 'components/views/offer_configurator/components/common';

// Import types
import type { PacketSubtype } from 'types/api/products/product';
import type { SummaryProduct } from 'types/views/configurator';

// Import hooks
import { useAppSelector } from 'store/hooks';
import { useConfiguratorContext } from 'components/context/configurator/ConfiguratorProvider';
import { useSummaryStore } from 'components/views/offer_configurator/hooks/useSummaryStore';

// Import helpers
import { fetchCalculatePrice } from 'helpers/configurator';
import { isPacketsEqual } from 'components/views/offer_configurator/helpers/packets';

type Products = Partial<Record<PacketSubtype, SummaryProduct[]>>;

export const useBasket = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [premium, action, summary, setSummary] = useSummaryStore(
		useShallow(({ premium, action, summary, setSummary }) => [premium, action, summary, setSummary])
	);

	const { subscription, selectedMain, settings } = useConfiguratorContext();

	const packets = useAppSelector(({ configurator }) => configurator.packets);

	// variable not used but may come in handy in the future
	const isBasketAndSubscriptionEqual =
		action === 'add_additional_products' && premium.every(({ uuid }) => subscription?.premium.find(isPacketsEqual(uuid)));

	const products: Products = _.groupBy(summary?.products, 'subtype');

	const inAdvance = useMemo(() => {
		if (!settings || !subscription) return false;
		const billingDaysLeft = differenceInCalendarDays(subscription.till, new Date());
		return billingDaysLeft <= settings.minimum_period_days;
	}, [settings, subscription]);

	const onDetailsButtonClick = () => {
		if (!summary) return;
		NiceModal.show(PaymentSchedule, { summary });
	};

	useEffect(() => {
		const unsubscribe = useSummaryStore.subscribe(
			(state) => ({ main: state.main, premium: state.premium, action: state.action }),
			({ main, premium, action }) => {
				const products = main ? [main, ...premium] : premium;

				if (!action) return;

				setIsLoading(true);

				fetchCalculatePrice(products, action)
					.then((calculated) => ({
						...calculated,
						products: calculated.products.map((product) => {
							const packet =
								product.uuid === main?.uuid
									? packets.main.find(({ uuid }) => uuid === product.uuid)
									: packets.premium.find(({ uuid }) => uuid === product.uuid);

							return { ...product, title: packet?.title, subtype: packet?.subtype };
						})
					}))
					.then(setSummary)
					.catch(() => setSummary(null))
					.finally(() => setIsLoading(false));
			},
			{ fireImmediately: true, equalityFn: shallow }
		);

		return () => unsubscribe();
	}, []);

	return { isLoading, summary, subscription, products, isBasketAndSubscriptionEqual, selectedMain, settings, inAdvance, onDetailsButtonClick };
};
